// frontend/src/utils/lazyRetry.js
//
// Pengganti React.lazy yang tidak langsung menyerah saat chunk halaman gagal
// dimuat. Tanpa ini, SATU request yang jatuh = layar "Terjadi Kesalahan":
// React.lazy menyimpan promise yang ditolak, jadi render ulang tidak pernah
// mencoba lagi — satu-satunya jalan keluar bagi kasir adalah tombol logout.
//
// Dua penyebab yang nyata di lapangan (tablet cabang):
//   1. Deploy baru → nama file chunk berubah, file lama hilang dari server,
//      tapi device masih menjalankan index.html versi lama dari cache.
//   2. Sinyal naik-turun → request chunk putus di tengah jalan.
//
// Urutannya: coba lagi sekali → kalau masih gagal dan memang ada build baru
// di server, muat ulang bersih (buang cache) supaya device pindah ke build
// baru → kalau tidak, biarkan gagal dan ErrorBoundary yang menampilkan pesan
// ramah ke user.
import { lazy } from 'react';
import { muatUlangBersih, adaBuildBaru } from './autoUpdate';

const KUNCI_RELOAD = 'ft_chunk_reloaded';
const JEDA_COBA_ULANG_MS = 800;

const tunggu = (ms) => new Promise((r) => setTimeout(r, ms));

function sudahPernahReload() {
  try {
    return sessionStorage.getItem(KUNCI_RELOAD) === '1';
  } catch {
    return false;
  }
}

function tandaiReload(nilai) {
  try {
    if (nilai) sessionStorage.setItem(KUNCI_RELOAD, '1');
    else sessionStorage.removeItem(KUNCI_RELOAD);
  } catch {}
}

export default function lazyRetry(factory) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Berhasil → izinkan satu reload lagi kalau nanti ada chunk lain yang
      // gagal. Penjaganya tetap ada: reload hanya boleh sekali per kegagalan,
      // tidak pernah dua kali beruntun tanpa ada muatan yang sukses.
      tandaiReload(false);
      return mod;
    } catch (err) {
      await tunggu(JEDA_COBA_ULANG_MS);
      try {
        const mod = await factory();
        tandaiReload(false);
        return mod;
      } catch (err2) {
        // Offline: JANGAN muat ulang bersih. Membuang cache saat tidak ada
        // jaringan berarti app shell ikut hilang dan kasir tidak bisa membuka
        // apa pun — jauh lebih parah daripada satu layar error.
        if (!navigator.onLine) throw err2;
        if (sudahPernahReload()) throw err2;

        const buildBaru = await adaBuildBaru();
        if (buildBaru !== true) throw err2;

        tandaiReload(true);
        muatUlangBersih();
        // Tahan render sampai halaman benar-benar dimuat ulang, supaya layar
        // error tidak sempat berkedip sepersekian detik sebelum reload.
        return new Promise(() => {});
      }
    }
  });
}
